import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs/operators';

import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import { ErrorLibService } from 'src/shared/services/error-lib.service';
import { ServiceOrderService } from 'src/shared/services/service-order.service';
import { Unsubscriber } from '../../unsubscriber/unsubscriber.component';
import { Installation, ServiceOrder, User, mapUserNode, shortDate, toFloat } from '../models/service-order.model';
import {
  PRIORITY_OPTIONS,
  PriorityMeta,
  EDIT_STATUS_OPTIONS,
  SelectOption,
  StatusMeta,
  buildSelectOptions,
  isClosedStatus,
  priorityMeta,
  statusMeta,
} from '../models/service-order.labels';
import { DetailSection, buildDetailSections } from '../models/service-order.sections';
import { ServiceOrderInstallationPanelComponent } from '../installation-panel/service-order-installation-panel.component';

export type DialogMode = 'view' | 'edit';

interface TimelineNode {
  labelKey: string;
  date: string | null;
  state: 'done' | 'issues' | 'current' | 'pending';
  /** Data registrada sem o status correspondente (o alerts-api grava started_at/closed_at
   *  sem tocar a coluna status, então OS "em progresso" aparecia com data de fechamento) **/
  inconsistent: boolean;
}

interface FactRow {
  key: string;
  labelKey: string;
  value: string | null;
}

/** Garante que o autocomplete devolveu um objeto selecionado, e não o texto digitado **/
function selectedObject(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  return value && typeof value === 'object' && value.id ? null : { notSelected: true };
}

/**
 * Ficha da OS: um único modal para ler e editar. Abre em leitura (clique na linha) ou já em
 * edição (lápis); o botão Editar alterna no mesmo modal. Iniciar/fechar são delegados à tela,
 * que fecha o modal antes de confirmar para não empilhar overlay sobre overlay.
 *
 * Layout em duas colunas com corpo de altura fixa: a coluna esquerda (contexto) tem sempre
 * as mesmas linhas e a direita (textos) rola — o modal não muda de tamanho ao navegar.
 */
@Component({
  selector: 'app-service-order-dialog',
  templateUrl: './service-order-dialog.component.html',
  styleUrls: ['./service-order-dialog.component.less'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    AutoCompleteModule,
    ButtonModule,
    DialogModule,
    MessageModule,
    ProgressBarModule,
    ProgressSpinnerModule,
    TagModule,
    TextareaModule,
    TooltipModule,
    ServiceOrderInstallationPanelComponent,
  ],
})
export class ServiceOrderDialogComponent extends Unsubscriber implements OnChanges {
  constructor(
    private formBuilder: FormBuilder,
    private serviceOrderService: ServiceOrderService,
    private errorLibService: ErrorLibService,
    private translate: TranslateService,
    private changeDetector: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {
    super();

    this.buildSelectOptions();
    // Os labels são traduzidos na montagem das opções, então trocar de idioma precisa refazê-las
    this.anotherSubscription = this.translate.onLangChange.subscribe(() => this.buildSelectOptions());
    this.watchStatusForCloseNotes();
  }

  @Input({ required: true }) serviceOrder!: ServiceOrder;

  /** Abre já em edição (lápis da tabela) **/
  @Input() startInEditMode: boolean = false;

  /** Navegação entre as OS da página atual sem fechar o modal **/
  @Input() hasPrevious: boolean = false;

  @Input() hasNext: boolean = false;

  /** Posição (1-based) e total na página, para o "n de N" do rodapé **/
  @Input() position: number = 0;

  @Input() total: number = 0;

  @Output() previous = new EventEmitter<void>();

  @Output() next = new EventEmitter<void>();

  @Output() closed = new EventEmitter<void>();

  @Output() saved = new EventEmitter<void>();

  /** Transições ficam na tela (confirmação + serviceOrderStart/Close); o modal só dispara **/
  @Output() start = new EventEmitter<string>();

  @Output() close = new EventEmitter<string>();

  public visible: boolean = true;

  public mode: DialogMode = 'view';

  public updateLoading: boolean = false;

  public installationFilterLoading: boolean = false;

  public userFilterLoading: boolean = false;

  /** Sugestões dos autocompletes, semeadas com o valor atual da OS **/
  public installationOptions: Installation[] = [];

  public userOptions: User[] = [];

  /** Só revela o campo de instalação depois de o usuário assumir a troca **/
  public installationChangeOpen: boolean = false;

  /**
   * Confirmação de descarte inline, no próprio rodapé. Confirmação em overlay sobre um modal
   * depende de z-index de duas bibliotecas: quando ela não aparece, a promise nunca resolve
   * e o modal fica preso aberto.
   **/
  public discardConfirmOpen: boolean = false;

  /** De onde veio o descarte: pelo X/ESC/máscara o confirmar fecha o modal; pelo Cancelar, volta à leitura **/
  private discardIntent: 'close' | 'view' = 'view';

  /** Aviso de sucesso inline, pelo mesmo motivo de não usar overlay sobre o modal **/
  public savedMessageVisible: boolean = false;

  private savedMessageTimer: ReturnType<typeof setTimeout> | null = null;

  public statusOptions: SelectOption[] = [];

  public priorityOptions: SelectOption[] = [];

  public shortDate = shortDate;

  public form: FormGroup = this.formBuilder.group({
    selectedInstallation: [null, [Validators.required, selectedObject]],
    selectedUser: [null],
    status: [null, Validators.required],
    priority: [null, Validators.required],
    problems: [null],
    notes: [null],
    closeNotes: [null],
    withIssues: [false],
  });

  ngOnChanges(): void {
    this.resetForm();
    this.visible = true;
    this.mode = this.startInEditMode && !this.isClosed ? 'edit' : 'view';
    this.loadInstallationDetails();
  }

  // ------------------------------------------------- painel da instalação

  /** Instalação da OS com endereço/equipamento/gateway (o node da OS não traz isso) **/
  public installationDetails: Installation | null = null;

  public installationDetailsLoading: boolean = false;

  /**
   * O painel mostra a instalação escolhida no autocomplete assim que ela é selecionada (o
   * objeto já vem completo da busca); fora disso, a instalação gravada na OS.
   **/
  public get panelInstallation(): Installation | null {
    const selected = this.form.get('selectedInstallation')?.value;

    if (this.isEditing && selected && typeof selected === 'object' && selected.details) {
      return selected as Installation;
    }

    return this.installationDetails;
  }

  /**
   * Hidrata a instalação (o node da OS só traz id, referência e MAC). O serviço decide a fonte e
   * busca pelo ID, com a referência como fallback; a plaqueta renomeada continua sendo achada e
   * o painel avisa que a referência mudou.
   **/
  private loadInstallationDetails(): void {
    const serviceOrder = this.serviceOrder;
    const id = serviceOrder?.installation?.id || null;
    const reference = serviceOrder?.installation?.reference || null;

    this.installationDetails = null;
    this.installationDetailsLoading = !!(id || reference);

    if (!id && !reference) {
      return;
    }

    this.anotherSubscription = this.serviceOrderService.hydrateInstallation(serviceOrder.installation).subscribe({
      next: (installation: Installation | null) => {
        // Resposta de uma OS anterior (navegação ‹ › rápida) não pode sobrescrever a atual
        if (this.serviceOrder !== serviceOrder) {
          return;
        }

        this.installationDetails = installation;
        this.installationDetailsLoading = false;
      },
      error: () => {
        // Painel é contexto, não bloqueia a ficha: silencia e mostra "não encontrada"
        if (this.serviceOrder === serviceOrder) {
          this.installationDetailsLoading = false;
        }
      },
    });
  }

  /**
   * Semeia as listas com o valor atual: o autocomplete exibe o que já está gravado e o
   * submit tem os 5 campos da instalação sem exigir nova busca.
   **/
  private resetForm(): void {
    const serviceOrder = this.serviceOrder;

    this.installationOptions = serviceOrder?.installation ? [serviceOrder.installation] : [];
    this.userOptions = serviceOrder?.assignee?.id ? [serviceOrder.assignee] : [];

    this.form.reset({
      selectedInstallation: serviceOrder?.installation ?? null,
      selectedUser: serviceOrder?.assignee?.id ? serviceOrder.assignee : null,
      status: serviceOrder?.status ?? null,
      priority: serviceOrder?.priority ?? null,
      problems: serviceOrder?.problems ?? null,
      notes: serviceOrder?.notes ?? null,
      closeNotes: null,
      withIssues: false,
    });

    this.installationChangeOpen = false;
    this.discardConfirmOpen = false;
  }

  private buildSelectOptions(): void {
    this.statusOptions = buildSelectOptions(EDIT_STATUS_OPTIONS, statusMeta, (key) => this.translate.instant(key));
    this.priorityOptions = buildSelectOptions(PRIORITY_OPTIONS, priorityMeta, (key) => this.translate.instant(key));
  }

  /**
   * Os detalhes e o checkbox "Com pendência" só aparecem enquanto o status escolhido for
   * Finalizada, e somem (sem valor) se o usuário voltar atrás. Sem pendência os detalhes são
   * opcionais (o alerts-api aceita close_notes vazio); com pendência passam a ser obrigatórios.
   **/
  private watchStatusForCloseNotes(): void {
    const closeNotesControl = this.form.get('closeNotes');
    const withIssuesControl = this.form.get('withIssues');

    const syncCloseNotesRule = () => {
      if (this.closeNotesRequired) {
        closeNotesControl?.setValidators(Validators.required);
      } else {
        closeNotesControl?.clearValidators();
      }

      closeNotesControl?.updateValueAndValidity({ emitEvent: false });
    };

    this.anotherSubscription = this.form.get('status')!.valueChanges.subscribe(() => {
      if (!this.requiresCloseNotes) {
        closeNotesControl?.setValue(null, { emitEvent: false });
        withIssuesControl?.setValue(false, { emitEvent: false });
      }

      syncCloseNotesRule();
    });

    this.anotherSubscription = withIssuesControl!.valueChanges.subscribe(() => syncCloseNotesRule());
  }

  // ---------------------------------------------------------------- estado

  public get isEditing(): boolean {
    return this.mode === 'edit';
  }

  public get isClosed(): boolean {
    return isClosedStatus(this.serviceOrder?.status ?? null);
  }

  /** Só pede relatório quando a edição está de fato fechando uma OS que estava aberta **/
  public get requiresCloseNotes(): boolean {
    return isClosedStatus(this.form.get('status')?.value ?? null) && !this.isClosed;
  }

  /** Só a pendência exige os detalhes **/
  public get closeNotesRequired(): boolean {
    return this.requiresCloseNotes && !!this.form.get('withIssues')?.value;
  }

  /** Status que a edição vai gravar: Finalizada + pendência marcada vira CLOSED_WITH_ISSUES **/
  private get effectiveStatus(): string | null {
    const status = this.form.get('status')?.value ?? null;

    return status === 'CLOSED' && this.form.get('withIssues')?.value ? 'CLOSED_WITH_ISSUES' : status;
  }

  /** Erro inline: obrigatório, vazio e já mexido (Salvar fica desabilitado de qualquer forma) **/
  public get closeNotesError(): boolean {
    const control = this.form.get('closeNotes');

    return this.closeNotesRequired && !!control?.invalid && !!control?.dirty;
  }

  /** Cabeçalho acompanha o formulário durante a edição: preview sem salto de layout **/
  public get statusMeta(): StatusMeta {
    const status = this.isEditing ? this.effectiveStatus : this.serviceOrder?.status;

    return statusMeta(status ?? null);
  }

  public get priorityMeta(): PriorityMeta {
    const priority = this.isEditing ? this.form.get('priority')?.value : this.serviceOrder?.priority;

    return priorityMeta(priority ?? null);
  }

  // --------------------------------------------------------------- leitura

  /** Seções de texto sempre presentes (com "—" quando vazias): segura a altura entre OS **/
  public get sections(): DetailSection[] {
    return buildDetailSections(this.serviceOrder);
  }

  public get facts(): FactRow[] {
    const rows: FactRow[] = [
      {
        key: 'assignee',
        labelKey: 'service-order.assigned-to',
        value: this.serviceOrder?.assignee?.displayName ?? null,
      },
      {
        // OS criadas em lote vêm sem user: o batch do alerts-api grava user = 0
        key: 'createdBy',
        labelKey: 'service-order.created-by-label',
        value: this.serviceOrder?.user?.displayName || null,
      },
    ];

    // chAlertId é quase sempre nulo: só ocupa espaço quando existe
    if (this.serviceOrder?.chAlertId) {
      rows.push({
        key: 'chAlertId',
        labelKey: 'service-order.alert-id-label',
        value: this.serviceOrder.chAlertId,
      });
    }

    return rows;
  }

  /**
   * Linha do tempo criada -> iniciada -> fechada. O estado vem do `status`, não do timestamp:
   * o alerts-api grava started_at/closed_at sem atualizar status, então uma OS "Em progresso"
   * com closed_at preenchido aparecia como concluída, contradizendo o badge da linha.
   **/
  public get timeline(): TimelineNode[] {
    const serviceOrder = this.serviceOrder;
    const status = serviceOrder?.status ?? null;
    const closed = isClosedStatus(status);
    const started = status === 'IN_PROGRESS' || closed;

    return [
      {
        labelKey: 'service-order.timeline-created',
        date: serviceOrder?.createdAt ?? null,
        state: serviceOrder?.createdAt ? 'done' : 'pending',
        inconsistent: false,
      },
      {
        labelKey: 'service-order.timeline-started',
        date: serviceOrder?.startedAt ?? null,
        state: started ? 'done' : status === 'OPEN' ? 'current' : 'pending',
        inconsistent: !!serviceOrder?.startedAt && !started,
      },
      {
        labelKey: 'service-order.timeline-closed',
        date: serviceOrder?.closedAt ?? null,
        state: closed
          ? (status === 'CLOSED_WITH_ISSUES' ? 'issues' : 'done')
          : status === 'IN_PROGRESS' ? 'current' : 'pending',
        inconsistent: !!serviceOrder?.closedAt && !closed,
      },
    ];
  }

  // ---------------------------------------------------------------- edição

  public enterEditMode(): void {
    if (this.isClosed) {
      return;
    }

    this.resetForm();
    this.mode = 'edit';
  }

  public openInstallationChange(): void {
    this.installationChangeOpen = true;
  }

  /** Desiste da troca: devolve a instalação original e fecha o editor do cabeçalho **/
  public cancelInstallationChange(): void {
    const installation = this.serviceOrder?.installation ?? null;

    this.installationOptions = installation ? [installation] : [];
    this.form.get('selectedInstallation')?.setValue(installation);
    this.installationChangeOpen = false;
  }

  public searchInstallation(event: { query: string }): void {
    this.installationFilterLoading = true;

    // Referência da instalação ou serial do equipamento: o serviço resolve os dois
    this.anotherSubscription = this.serviceOrderService
      .searchInstallations(event?.query)
      .subscribe({
        next: (installations: Installation[]) => {
          this.installationOptions = installations;
          this.installationFilterLoading = false;
        },
        error: (error: any) => {
          this.installationFilterLoading = false;
          this.errorLibService.errorAlert(error);
        },
      });
  }

  public searchUser(event: { query: string }): void {
    this.userFilterLoading = true;

    this.anotherSubscription = this.serviceOrderService
      .searchUsers(event?.query)
      .subscribe({
        next: (users: User[]) => {
          this.userOptions = users;
          this.userFilterLoading = false;
        },
        error: (error: any) => {
          this.userFilterLoading = false;
          this.errorLibService.errorAlert(error);
        },
      });
  }

  public updateServiceOrder(): void {
    if (!this.form.valid) {
      return;
    }

    const value = this.form.getRawValue();
    const installation: Installation | null = value?.selectedInstallation ?? null;

    this.updateLoading = true;

    /**
     * closeNotes só é gravado pela mutation serviceOrderClose (não existe no input do
     * serviceOrderUpdate nem no PATCH do alerts-api). Quando a edição fecha a OS, o close vai
     * primeiro e já grava closed_at, close_notes e o status final (com ou sem ressalva); o
     * update em seguida grava o resto e não repete o status.
     */
    const closing = this.requiresCloseNotes;

    const update$ = this.serviceOrderService
      .updateServiceOrder(this.serviceOrder.id, {
        status: closing ? null : value?.status ?? null,
        priority: value?.priority ?? null,
        installationId: installation?.id ?? null,
        installationReference: installation?.reference ?? null,
        latitude: toFloat(installation?.latitude),
        longitude: toFloat(installation?.longitude),
        deviceMac: installation?.deviceMac ?? null,
        assignee: value?.selectedUser?.id ?? null,
        problems: value?.problems ?? '',
        notes: value?.notes ?? '',
      });

    const save$ = closing
      ? this.serviceOrderService
        .closeServiceOrder(this.serviceOrder.id, value?.closeNotes ?? '', !!value?.withIssues)
        .pipe(switchMap(() => update$))
      : update$;

    this.anotherSubscription = save$.subscribe({
      next: () => {
        this.updateLoading = false;

        // Reflete o que foi salvo e volta para a leitura no mesmo modal
        this.applySavedValues(value, installation);
        this.resetForm();
        this.mode = 'view';
        this.showSavedMessage();
        this.saved.emit();
      },
      error: (error: any) => {
        this.updateLoading = false;
        this.errorLibService.errorAlert(error);
      },
    });
  }

  private applySavedValues(value: any, installation: Installation | null): void {
    if (value?.closeNotes) {
      this.serviceOrder.closeNotes = value.closeNotes;
    }

    this.serviceOrder.status = this.effectiveStatus ?? value?.status;
    this.serviceOrder.priority = value?.priority;
    this.serviceOrder.problems = value?.problems ?? null;
    this.serviceOrder.notes = value?.notes ?? null;
    this.serviceOrder.assignee = value?.selectedUser ?? null;

    if (installation) {
      this.serviceOrder.installation = installation;
    }
  }

  private showSavedMessage(): void {
    this.savedMessageVisible = true;

    if (this.savedMessageTimer) {
      clearTimeout(this.savedMessageTimer);
    }

    this.savedMessageTimer = setTimeout(() => {
      this.savedMessageVisible = false;
      this.savedMessageTimer = null;
    }, 3500);
  }

  // -------------------------------------------------------------- navegação

  /** Só navega em leitura: em edição a troca de OS descartaria o formulário sem aviso **/
  public get canNavigate(): boolean {
    return !this.isEditing;
  }

  public goPrevious(): void {
    if (this.canNavigate && this.hasPrevious) {
      this.previous.emit();
    }
  }

  public goNext(): void {
    if (this.canNavigate && this.hasNext) {
      this.next.emit();
    }
  }

  /** Setas do teclado navegam entre as OS, exceto quando o foco está num campo de texto **/
  /**
   * Clique na máscara (área escura fora do card). O PrimeNG só fecha incondicionalmente, então
   * o listener é nosso: só reage à máscara deste dialog, e passa pela guarda de edição suja.
   **/
  @HostListener('document:mousedown', ['$event'])
  public onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (!this.visible || !target?.classList?.contains('p-dialog-mask')) {
      return;
    }

    if (this.host.nativeElement.contains(target)) {
      this.requestClose();
    }
  }

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (!this.visible) {
      return;
    }

    if (event.key === 'Escape') {
      this.onEscape();
      return;
    }

    if (!this.canNavigate) {
      return;
    }

    const target = event.target as HTMLElement | null;

    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goPrevious();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goNext();
    }
  }

  // ------------------------------------------------------------- fechamento

  /** Cancelar edição: pede confirmação no rodapé quando há alteração pendente **/
  public cancelEdit(): void {
    if (!this.form.dirty) {
      this.leaveEditMode();
      return;
    }

    this.discardIntent = 'view';
    this.discardConfirmOpen = true;
  }

  /** Confirma o descarte: fecha o modal se o pedido veio do X, senão volta para a leitura **/
  public confirmDiscard(): void {
    this.leaveEditMode();

    if (this.discardIntent === 'close') {
      this.closeDialog();
    }
  }

  /** Desiste de descartar e volta ao formulário **/
  public dismissDiscard(): void {
    this.discardConfirmOpen = false;
  }

  private leaveEditMode(): void {
    this.resetForm();
    this.mode = 'view';
  }

  /** Fechar o modal: em edição suja, primeiro confirma o descarte **/
  /**
   * ESC: mesmo caminho do X. Ignorado enquanto um overlay do autocomplete ou um Swal estiver
   * aberto, porque o ESC pertence a eles nesse momento.
   **/
  private onEscape(): void {
    if (document.querySelector('.p-autocomplete-overlay, .swal2-container')) {
      return;
    }

    if (this.discardConfirmOpen) {
      this.dismissDiscard();
      return;
    }

    this.requestClose();
  }

  public requestClose(): void {
    if (this.isEditing && this.form.dirty) {
      this.discardIntent = 'close';
      this.discardConfirmOpen = true;
      this.visible = true;
      return;
    }

    this.closeDialog();
  }

  /** Iniciar/fechar OS: o modal sai de cena antes de a tela abrir a confirmação **/
  public requestStart(): void {
    const id = this.serviceOrder.id;

    this.closeDialog();
    this.start.emit(id);
  }

  public requestFinish(): void {
    const id = this.serviceOrder.id;

    this.closeDialog();
    this.close.emit(id);
  }

  private closeDialog(): void {
    this.visible = false;
    this.closed.emit();
  }

  /**
   * Fechamento disparado pelo próprio p-dialog (ESC, X, clique fora).
   *
   * O clique na máscara chega por um listener que o PrimeNG registra com renderer.listen durante
   * a animação de entrada, fora do ciclo normal de eventos do template: o estado mudava
   * (closed emitido, pai zerando a OS selecionada) mas nenhuma detecção de mudanças rodava e o
   * modal ficava na tela. markForCheck agenda a detecção e a árvore do pai é reavaliada.
   **/
  public onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.visible = true;
      this.requestClose();
      this.changeDetector.markForCheck();
    }
  }
}
